import { supabase } from '@/lib/supabase';

/**
 * Client helper for the `auth-guard` edge function.
 *
 * Every authentication action (login, register, password reset) must clear the
 * guard BEFORE the Supabase auth call runs. The guard:
 *   * verifies the Cloudflare Turnstile token server-side with
 *     TURNSTILE_SECRET_KEY (the widget alone is never treated as proof)
 *   * enforces per-IP and per-email throttling with mkt_rate_hit
 *     (5 failures / 15 min => temporary lockout)
 *   * writes every failure to mkt_security_events with a hashed IP
 *
 * Failing OPEN is deliberate for transport errors only: if the function itself
 * is unreachable we do not lock legitimate users out of the product. An
 * explicit `rate_limited` / `captcha_failed` verdict always blocks.
 */
export type AuthKind = 'login' | 'register' | 'reset';

export interface GuardVerdict {
  ok: boolean;
  /** 'rate_limited' | 'captcha_failed' | '' */
  reason: string;
  /** Seconds until the lockout expires. */
  retryAfter: number;
}

const PASS: GuardVerdict = { ok: true, reason: '', retryAfter: 0 };

export const guardAuth = async (
  kind: AuthKind,
  email: string,
  token: string,
): Promise<GuardVerdict> => {
  try {
    const { data, error } = await supabase.functions.invoke('auth-guard', {
      body: { action: 'check', kind, email, token },
    });
    // Transport error with no verdict body => fail open (see note above).

    if (error && !data) return PASS;
    if (data?.ok) return PASS;
    return {
      ok: false,
      reason: String(data?.error || 'rate_limited'),
      retryAfter: Number(data?.retryAfter || 0),
    };
  } catch {
    return PASS;
  }
};

/** Burn a strike after a rejected credential / failed auth call. */
export const reportAuthFailure = async (kind: AuthKind, email: string) => {
  try {
    await supabase.functions.invoke('auth-guard', { body: { action: 'fail', kind, email } });
  } catch {
    /* non-blocking */
  }
};

/** Clear the counters after a genuine success. */
export const reportAuthSuccess = async (kind: AuthKind, email: string) => {
  try {
    await supabase.functions.invoke('auth-guard', { body: { action: 'ok', kind, email } });
  } catch {
    /* non-blocking */
  }
};

/** Human-readable "try again in X minutes" copy, EN + ES. */
export const guardMessage = (
  verdict: GuardVerdict,
  t: (k: string, vars?: Record<string, string | number>) => string,
) => {
  if (verdict.reason === 'captcha_failed') return t('auth.errCaptcha');
  const minutes = Math.max(Math.ceil((verdict.retryAfter || 60) / 60), 1);
  return t('auth.errTooMany', { minutes });
};
